import { useEffect, useRef, useState } from 'react'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'
import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { WorkflowRoleSelectorDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { ApprovalSettingsModal } from './ApprovalSettingsModal'
import { ApproverSelection } from './ApproverSelection'
import { type StrategyItem, getStrategyType } from './manualApproveHandlerUtils'
import {
  StrategySelector,
  type StrategySelectorEmployee,
  type StrategySelectorTeam,
} from './StrategySelector'

interface ApproverSelectionContainerProps {
  strategies: StrategyItem[]
  onAddStrategies: (strategies: StrategyItem[]) => void
  onDeleteStrategy: (strategyId: string) => void
  fallbackStrategies: StrategyItem[]
  onSetFallbackStrategy: (strategy: StrategyItem | null) => void
  availableEmployees: StrategySelectorEmployee[]
  availableTeams: StrategySelectorTeam[]
  employeesLoading: boolean
  teamsLoading: boolean
}

export const ApproverSelectionContainer = ({
  strategies,
  onAddStrategies,
  onDeleteStrategy,
  fallbackStrategies,
  onSetFallbackStrategy,
  availableEmployees,
  availableTeams,
  employeesLoading,
  teamsLoading,
}: ApproverSelectionContainerProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { company } = useCurrentSession()
  const hasInitializedFallback = useRef(false)

  const { data: rolesData } = useQuery(WorkflowRoleSelectorDocument, {
    variables: { companyId: company.id },
  })
  const availableRoles = rolesData?.jobCatalog?.rolesConnection?.nodes || []
  const treeRoles = rolesData?.jobCatalog?.treeNodesConnection?.nodes || []

  const treeRoleNameMap = new Map<string, string>()
  treeRoles.forEach((role) => {
    if (role.uuid && role.name) {
      treeRoleNameMap.set(role.uuid, role.name)
    }
  })

  const currentFallback = fallbackStrategies[0] ?? null
  const currentFallbackType = currentFallback?.type ?? ''

  const [fallbackStrategyType, setFallbackStrategyType] = useState<string>(currentFallbackType)

  useEffect(() => {
    if (currentFallbackType && !hasInitializedFallback.current) {
      setFallbackStrategyType(currentFallbackType)
      hasInitializedFallback.current = true
    }
  }, [currentFallbackType])

  const handleFallbackStrategyChange = (strategyValue: string) => {
    setFallbackStrategyType(strategyValue)

    // If it's a 'none' type (employee_manager, admins), set immediately
    const uiType = getStrategyType(strategyValue)
    if (uiType === 'none') {
      onSetFallbackStrategy({
        id: `fallback_${strategyValue}`,
        type: strategyValue,
      })
    } else {
      // Clear fallback when changing to a type that requires selection
      onSetFallbackStrategy(null)
    }
  }

  const handleFallbackEmployeeSelect = (
    selection: EntitySelectEntity[] | EntitySelectEntity | null
  ) => {
    if (selection === null) {
      onSetFallbackStrategy(null)
      return
    }

    const employeeId = Array.isArray(selection) ? selection[0]?.id : selection.id
    if (typeof employeeId === 'number') {
      onSetFallbackStrategy({
        id: `fallback_manual_selection_${employeeId}`,
        type: 'manual_selection',
        employeeId: employeeId,
      })
    }
  }

  const handleFallbackTeamSelect = (
    selection: EntitySelectEntity[] | EntitySelectEntity | null
  ) => {
    if (selection === null) {
      onSetFallbackStrategy(null)
      return
    }

    const teamId = Array.isArray(selection) ? selection[0]?.id : selection.id
    if (typeof teamId === 'number') {
      onSetFallbackStrategy({
        id: `fallback_${fallbackStrategyType}_${teamId}`,
        type: fallbackStrategyType,
        teamId: teamId,
      })
    }
  }

  const selectedFallbackEmployee = currentFallback?.employeeId
    ? availableEmployees
        .filter((emp) => emp.id === currentFallback.employeeId)
        .map((emp) => ({
          id: emp.id,
          name: emp.fullName,
          avatar: buildUrl(emp.avatar?.url),
        }))
    : []

  const selectedFallbackTeam = currentFallback?.teamId
    ? availableTeams
        .filter((team) => team.id === currentFallback.teamId)
        .map((team) => ({
          id: team.id,
          name: team.name,
        }))
    : []

  return (
    <Box gap="s8" width="full">
      <Text color="textPrimary" size="300" weight="medium">
        {i18n.t('workflows.sidepanel.approval.approvers.title')}
      </Text>

      {strategies.length > 0 && (
        <Box gap="s2" width="full">
          {strategies.map((strategy) => (
            <ApproverSelection
              key={strategy.id}
              strategy={strategy}
              onDelete={onDeleteStrategy}
              availableEmployees={availableEmployees}
              availableTeams={availableTeams}
              availableRoles={availableRoles}
              treeRoleNameMap={treeRoleNameMap}
            />
          ))}
        </Box>
      )}

      <Box flexDirection="row" justifyContent="flexStart" width="full" paddingBottom="s8">
        <Button
          size="md"
          label={i18n.t('workflows.sidepanel.approval.add_approver')}
          icon={Plus}
          variant="outline"
          onClick={() => setIsModalOpen(true)}
        />
      </Box>

      <Divider />

      <Box flexDirection="column" width="full" paddingTop="s8" gap="s8">
        <Text color="textPrimary" size="300" weight="medium">
          {i18n.t('workflows.sidepanel.approval.fallback.title')}
        </Text>

        <StrategySelector
          strategyValue={fallbackStrategyType}
          onStrategyChange={handleFallbackStrategyChange}
          onEmployeeSelect={handleFallbackEmployeeSelect}
          onTeamSelect={handleFallbackTeamSelect}
          selectedEmployees={selectedFallbackEmployee}
          selectedTeams={selectedFallbackTeam}
          availableEmployees={availableEmployees}
          availableTeams={availableTeams}
          employeesLoading={employeesLoading}
          teamsLoading={teamsLoading}
          singleEmployeeSelection
          placeholder={i18n.t('workflows.sidepanel.approval.fallback.placeholder')}
        />
      </Box>

      <ApprovalSettingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddStrategies={onAddStrategies}
        availableEmployees={availableEmployees}
        availableTeams={availableTeams}
        employeesLoading={employeesLoading}
        teamsLoading={teamsLoading}
        existingStrategies={strategies}
      />
    </Box>
  )
}
